<template>
  <!--
    LinkedIn-style docked chat. A fixed bottom-right rail that holds:
      • one window per open 1-on-1 conversation (left of the inbox)
      • a persistent "Messaging" inbox window (far right)
    Each window can be minimized to just its header bar, expanded back,
    closed, or have its options menu opened. Desktop only — on mobile
    (≤768px) the existing full-page /chat route is used instead.
  -->
  <div
    v-if="showDock"
    class="cdock"
    role="region"
    :aria-label="$t('Components.ChatDock.activeConversations')"
  >
    <!-- ── Individual 1-on-1 windows ───────────────────────────────── -->
    <section
      v-for="w in dockWindows"
      :key="'win-' + w.userId"
      class="cdock-win"
      :class="{ 'cdock-win--min': w.minimized }"
    >
      <header
        class="cdock-win__head"
        @click="w.minimized ? expandWindow(w) : null"
      >
        <div class="cdock-win__id">
          <span class="cdock-avatar">
            <Avatar
              :image="userFor(w.userId).Picture"
              :circle="true"
              :name="fullName(userFor(w.userId))"
              :alt="fullName(userFor(w.userId))"
              class="cdock-avatar__img"
            />
            <span
              v-if="isOnline(w.userId)"
              class="cdock-dot"
              :aria-label="$t('Components.ChatDock.online')"
            ></span>
          </span>
          <span class="cdock-win__name" :title="fullName(userFor(w.userId))">
            {{
              fullName(userFor(w.userId)) ||
              $t('Components.ChatDock.conversation')
            }}
          </span>
          <span v-if="unreadCountFor(w.userId)" class="cdock-badge">{{
            unreadCountFor(w.userId)
          }}</span>
        </div>

        <div class="cdock-win__actions" @click.stop>
          <!-- options menu -->
          <div class="cdock-menu-wrap">
            <button
              type="button"
              class="cdock-icon-btn"
              :aria-label="$t('Components.ChatDock.conversationOptions')"
              @click="toggleMenu(w.userId)"
            >
              <i class="fas fa-ellipsis-h"></i>
            </button>
            <div v-if="openMenuId === w.userId" class="cdock-menu" @click.stop>
              <button
                v-if="unreadCountFor(w.userId)"
                type="button"
                class="cdock-menu__item"
                @click="markRead(w)"
              >
                <i class="far fa-envelope-open"></i>
                {{ $t('Components.ChatDock.markAsRead') }}
              </button>
              <button
                type="button"
                class="cdock-menu__item"
                @click="viewProfile(w)"
              >
                <i class="far fa-user"></i>
                {{ $t('Components.ChatDock.viewProfile') }}
              </button>
              <button
                type="button"
                class="cdock-menu__item cdock-menu__item--danger"
                @click="deleteConversation(w)"
              >
                <i class="far fa-trash-alt"></i>
                {{ $t('Components.ChatDock.deleteConversation') }}
              </button>
            </div>
          </div>
          <!-- expand to full view / modal -->
          <button
            type="button"
            class="cdock-icon-btn"
            :aria-label="$t('Components.ChatDock.openFullView')"
            :title="$t('Components.ChatDock.openFullView')"
            @click="openFullView(w)"
          >
            <i class="fas fa-expand"></i>
          </button>
          <!-- minimize / expand -->
          <button
            type="button"
            class="cdock-icon-btn"
            :aria-label="
              w.minimized
                ? $t('Components.ChatDock.expand')
                : $t('Components.ChatDock.minimize')
            "
            @click="toggleMinimize(w)"
          >
            <i
              class="fas"
              :class="w.minimized ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </button>
          <!-- close -->
          <button
            type="button"
            class="cdock-icon-btn"
            :aria-label="$t('Views.Stage.ParticipantsModal.close')"
            @click="closeWindow(w)"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </header>

      <template v-if="!w.minimized">
        <div
          class="cdock-win__msgs"
          :data-user="w.userId"
          aria-live="polite"
          role="log"
        >
          <div v-if="!messagesFor(w.userId).length" class="cdock-empty">
            <i class="far fa-comment-dots"></i>
            <p>
              {{ $t('Components.ChatDock.noMessagesYet') }}<br />{{
                $t('Components.ChatDock.sayHello')
              }}
            </p>
          </div>
          <div
            v-for="msg in messagesFor(w.userId)"
            :key="msg.ChatMessageID"
            class="cdock-msg"
            :class="isOwn(msg) ? 'cdock-msg--own' : 'cdock-msg--other'"
          >
            <div class="cdock-bubble" v-html="renderMessage(msg.Message)"></div>
            <div class="cdock-msg__time">{{ formatTime(msg.CreatedAt) }}</div>
          </div>
        </div>

        <form class="cdock-input" @submit.prevent="send(w)">
          <input
            :value="drafts[w.userId] || ''"
            @input="setDraft(w.userId, $event.target.value)"
            type="text"
            class="cdock-input__field"
            :placeholder="$t('Components.Chat.ChatMain.writeAMessage')"
            :aria-label="
              $t('Components.ChatDock.messageTo', {
                name: fullName(userFor(w.userId))
              })
            "
          />
          <button
            type="submit"
            class="cdock-send"
            :disabled="
              !(drafts[w.userId] && drafts[w.userId].trim()) ||
              sendingId === w.userId
            "
            :aria-label="$t('Components.ChatDock.send')"
          >
            <i
              v-if="sendingId === w.userId"
              class="fas fa-circle-notch fa-spin"
            ></i>
            <i v-else class="far fa-paper-plane"></i>
          </button>
        </form>
      </template>
    </section>

    <!-- ── Persistent "Messaging" inbox window ─────────────────────── -->
    <section
      v-if="inboxOpen"
      class="cdock-win cdock-inbox"
      :class="{ 'cdock-win--min': inboxMinimized }"
    >
      <header
        class="cdock-win__head"
        @click="inboxMinimized ? expandInbox() : null"
      >
        <div class="cdock-win__id">
          <span class="cdock-avatar cdock-avatar--group">
            <i class="fas fa-comment-dots"></i>
            <span
              class="cdock-dot"
              :aria-label="$t('Components.ChatDock.online')"
            ></span>
          </span>
          <span class="cdock-win__name">{{
            $t('Components.ChatDock.directMessage')
          }}</span>
          <span v-if="totalUnread" class="cdock-badge">{{ totalUnread }}</span>
        </div>
        <div class="cdock-win__actions" @click.stop>
          <!-- options menu — wrapped so the dropdown positions relative to
               the button, not the whole (possibly tall) dock rail -->
          <div class="cdock-menu-wrap">
            <button
              type="button"
              class="cdock-icon-btn"
              :aria-label="$t('Components.ChatDock.options')"
              @click="toggleMenu('inbox')"
            >
              <i class="fas fa-ellipsis-h"></i>
            </button>
            <div
              v-if="openMenuId === 'inbox'"
              class="cdock-menu cdock-menu--right"
              @click.stop
            >
              <button
                type="button"
                class="cdock-menu__item"
                @click="refreshAndCloseMenu"
              >
                <i class="fas fa-sync"></i>
                {{ $t('Components.ChatDock.refresh') }}
              </button>
              <button
                type="button"
                class="cdock-menu__item"
                @click="closeInbox"
              >
                <i class="fas fa-times"></i>
                {{ $t('Components.ChatDock.hideMessaging') }}
              </button>
            </div>
          </div>
          <button
            type="button"
            class="cdock-icon-btn"
            :class="{ 'cdock-icon-btn--active': composeMode }"
            :aria-label="$t('Components.ChatDock.compose')"
            :aria-pressed="composeMode ? 'true' : 'false'"
            @click.stop="toggleCompose"
          >
            <i class="far fa-edit"></i>
          </button>
          <button
            type="button"
            class="cdock-icon-btn"
            :aria-label="
              inboxMinimized
                ? $t('Components.ChatDock.expand')
                : $t('Components.ChatDock.minimize')
            "
            @click="toggleInbox"
          >
            <i
              class="fas"
              :class="inboxMinimized ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </button>
        </div>
      </header>

      <div v-if="!inboxMinimized" class="cdock-inbox__body">
        <div class="cdock-search">
          <i class="fas fa-search"></i>
          <input
            v-model="search"
            type="text"
            :placeholder="$t('Components.ChatDock.searchMessages')"
            :aria-label="$t('Components.ChatDock.searchPeople')"
          />
        </div>

        <!-- Recent conversations -->
        <template v-if="!composeMode && filteredRecent.length">
          <div class="cdock-list-label">
            {{ $t('Components.ChatDock.recent') }}
          </div>
          <button
            v-for="chat in filteredRecent"
            :key="'rc-' + chat.ChatID"
            type="button"
            class="cdock-row"
            :class="{ 'cdock-row--unread': unreadInChat(chat) > 0 }"
            @click="openFromChat(chat)"
          >
            <span class="cdock-avatar cdock-avatar--sm">
              <Avatar
                :image="counterpart(chat).Picture"
                :circle="true"
                :name="fullName(counterpart(chat))"
                :alt="fullName(counterpart(chat))"
                class="cdock-avatar__img"
              />
              <span
                v-if="isOnline(counterpart(chat).UserID)"
                class="cdock-dot"
              ></span>
            </span>
            <span class="cdock-row__body">
              <span class="cdock-row__name">
                <span
                  v-if="unreadInChat(chat) > 0"
                  class="cdock-row__unread-dot"
                  :aria-label="$t('Components.ChatDock.unreadMessages')"
                ></span>
                {{ fullName(counterpart(chat)) }}
              </span>
              <span class="cdock-row__preview">{{ preview(chat) }}</span>
            </span>
            <span class="cdock-row__meta">
              <span class="cdock-row__time">{{ formatDay(lastAt(chat)) }}</span>
              <span v-if="unreadInChat(chat) > 0" class="cdock-row__count">
                {{ unreadInChat(chat) > 9 ? '9+' : unreadInChat(chat) }}
              </span>
            </span>
          </button>
        </template>

        <!-- All attendees -->
        <div class="cdock-list-label">
          {{
            composeMode
              ? $t('Components.ChatDock.startNewChat')
              : $t('Components.ChatDock.allAttendees')
          }}
        </div>
        <button
          v-for="u in filteredAttendees"
          :key="'at-' + u.UserID"
          type="button"
          class="cdock-row cdock-row--compact"
          @click="openWith(u)"
        >
          <span class="cdock-avatar cdock-avatar--sm">
            <Avatar
              :image="u.Picture"
              :circle="true"
              :name="fullName(u)"
              :alt="fullName(u)"
              class="cdock-avatar__img"
            />
            <span v-if="isOnline(u.UserID)" class="cdock-dot"></span>
          </span>
          <span class="cdock-row__body">
            <span class="cdock-row__name">{{ fullName(u) }}</span>
            <span v-if="u.Jobtitle || u.Company" class="cdock-row__preview">
              <template v-if="u.Jobtitle">{{ u.Jobtitle }}</template>
              <template v-if="u.Jobtitle && u.Company"> · </template>
              <template v-if="u.Company">{{ u.Company }}</template>
            </span>
          </span>
        </button>
        <div
          v-if="!filteredAttendees.length && !filteredRecent.length"
          class="cdock-inbox__empty"
        >
          <i class="far fa-user"></i>
          <p>{{ $t('Components.ChatDock.noOtherAttendees') }}</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import Vue from 'vue';
import Avatar from '@/components/shared/Avatar';
import PrivateChatModal from '@/components/PrivateChatModal';
import { getPusherInstance } from '@/utils';

export default {
  name: 'ChatDock',
  components: { Avatar },
  data() {
    return {
      drafts: {},
      sendingId: null,
      openMenuId: null,
      search: '',
      composeMode: false,
      isDesktop: true,
      pollTimer: null,
      onDocClick: null
    };
  },
  computed: {
    ...mapGetters('Authentication', { profile: 'getUser' }),
    ...mapGetters('CurrentEvent', ['features']),
    ...mapGetters('Chat', [
      'getChats',
      'getChatWithUser',
      'getDockWindows',
      'getDockUser',
      'isInboxOpen',
      'isInboxMinimized'
    ]),
    dockWindows() {
      return this.getDockWindows || [];
    },
    // 1-on-1 chat is available when any of the platform chat toggles is
    // on. Drives whether the persistent "Messaging" window is shown.
    chatEnabled() {
      const f = this.features || {};
      return !!(f.webAppChat || f.stageChatEnabled || f.lobbyChatEnabled);
    },
    // Show the inbox by default whenever chat is enabled (LinkedIn-style),
    // unless the user explicitly hid it via "Hide messaging".
    inboxOpen() {
      return this.chatEnabled && this.isInboxOpen;
    },
    inboxMinimized() {
      return this.isInboxMinimized;
    },
    showDock() {
      if (!this.isDesktop) return false;
      if (!this.profile || !this.profile.UserID) return false;
      return this.dockWindows.length > 0 || this.inboxOpen;
    },
    onlineUserIds() {
      return (
        (this.$store.state.Users && this.$store.state.Users.onlineUserIds) || []
      );
    },
    allUsers() {
      const items =
        (this.$store.state.Users && this.$store.state.Users.items) || [];
      return Array.isArray(items) ? items : Object.values(items);
    },
    recentChats() {
      return (this.getChats || []).slice().sort((a, b) => {
        return new Date(this.lastAt(b)) - new Date(this.lastAt(a));
      });
    },
    filteredRecent() {
      const q = (this.search || '').trim().toLowerCase();
      if (!q) return this.recentChats;
      return this.recentChats.filter((c) =>
        this.fullName(this.counterpart(c)).toLowerCase().includes(q)
      );
    },
    attendees() {
      const ownId = this.profile && this.profile.UserID;
      const inRecent = new Set(
        this.recentChats.map((c) => this.counterpart(c).UserID)
      );
      return this.allUsers.filter(
        (u) => u && u.UserID && u.UserID !== ownId && !inRecent.has(u.UserID)
      );
    },
    filteredAttendees() {
      const q = (this.search || '').trim().toLowerCase();
      let list = this.attendees;
      if (q) {
        list = list.filter((u) => {
          const name = this.fullName(u).toLowerCase();
          const co = (u.Company || '').toLowerCase();
          const job = (u.Jobtitle || '').toLowerCase();
          return name.includes(q) || co.includes(q) || job.includes(q);
        });
      }
      return list.slice().sort((a, b) => {
        const aOn = this.isOnline(a.UserID) ? 0 : 1;
        const bOn = this.isOnline(b.UserID) ? 0 : 1;
        if (aOn !== bOn) return aOn - bOn;
        return this.fullName(a).localeCompare(this.fullName(b));
      });
    },
    totalUnread() {
      try {
        return this.$store.getters['Chat/getUnreadChatsCount'] || 0;
      } catch (e) {
        return 0;
      }
    }
  },
  watch: {
    showDock(val) {
      if (val) {
        this.startPolling();
        // The dock can become visible after `created()` (e.g. the user
        // opens the inbox later), so load chats + attendees here too —
        // otherwise "Start a new chat" has an empty list.
        this.loadChatData().catch(() => {});
        this.ensureUsers();
      } else {
        this.stopPolling();
      }
    }
  },
  created() {
    this.isDesktop =
      typeof window !== 'undefined' ? window.innerWidth > 768 : true;
    this._onResize = () => {
      this.isDesktop = window.innerWidth > 768;
    };
    window.addEventListener('resize', this._onResize);
    // Close any open options menu when clicking elsewhere. Guard with a
    // `closest` check (instead of relying only on @click.stop) so a click
    // on a menu item never closes the menu before its handler runs.
    this.onDocClick = (e) => {
      const t = e && e.target;
      if (t && t.closest && t.closest('.cdock-menu, .cdock-menu-wrap')) {
        return;
      }
      this.openMenuId = null;
    };
    document.addEventListener('click', this.onDocClick);
    if (this.showDock) {
      this.startPolling();
      this.loadChatData().catch(() => {});
      this.ensureUsers();
    }
    try {
      getPusherInstance().bind('chat-new-message', this.onIncoming);
    } catch (e) {
      /* ignore */
    }
  },
  beforeDestroy() {
    this.stopPolling();
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
    try {
      getPusherInstance().unbind('chat-new-message', this.onIncoming);
    } catch (e) {
      /* ignore */
    }
  },
  methods: {
    ...mapActions('Chat', [
      'loadChatData',
      'createNewChat',
      'sendChatMessage',
      'activateChat',
      'markChatRead'
    ]),
    ...mapMutations('Chat', [
      'openDockWindow',
      'closeDockWindow',
      'setDockWindowMinimized',
      'setInboxOpen',
      'setInboxMinimized'
    ]),
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        this.loadChatData().catch(() => {});
      }, 5000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    ensureUsers() {
      if (this.allUsers.length) return;
      this.$store
        .dispatch('Users/fetchUsers', {
          page: 1,
          pageSize: 100,
          search: '',
          groupId: ''
        })
        .catch(() => {});
    },
    onIncoming() {
      this.loadChatData()
        .then(() => this.scrollAllToBottom())
        .catch(() => {});
    },
    // ── Lookups ───────────────────────────────────────────────────
    userFor(userId) {
      const cached = this.getDockUser(userId);
      if (cached) return cached;
      const fromUsers = this.allUsers.find((u) => u && u.UserID === userId);
      if (fromUsers) return fromUsers;
      const chat = this.getChatWithUser(userId);
      if (chat) return this.counterpart(chat);
      return {};
    },
    counterpart(chat) {
      if (!chat || !chat.Users) return {};
      const ownId = this.profile && this.profile.UserID;
      return (
        chat.Users.find((u) => u && u.UserID !== ownId) || chat.Users[0] || {}
      );
    },
    chatFor(userId) {
      return this.getChatWithUser ? this.getChatWithUser(userId) : null;
    },
    messagesFor(userId) {
      const chat = this.chatFor(userId);
      const list = (chat && chat.Messages) || [];
      return list
        .slice()
        .sort((a, b) => new Date(a.CreatedAt) - new Date(b.CreatedAt));
    },
    unreadCountFor(userId) {
      return this.unreadInChat(this.chatFor(userId));
    },
    // Count of messages in a chat sent by the other person that the
    // current user hasn't read yet.
    unreadInChat(chat) {
      if (!chat || !Array.isArray(chat.Messages)) return 0;
      const ownId = this.profile && this.profile.UserID;
      return chat.Messages.filter(
        (m) =>
          m.UserID !== ownId &&
          Array.isArray(m.readby) &&
          !m.readby.includes(ownId)
      ).length;
    },
    isOnline(userId) {
      if (!userId) return false;
      if (this.onlineUserIds.includes(userId)) return true;
      const u = this.allUsers.find((x) => x && x.UserID === userId);
      if (u) return u.Online === true || u.Online === 1 || u.Online === '1';
      return false;
    },
    fullName(user) {
      if (!user) return '';
      const f = user.Firstname || user.firstName || user.FirstName || '';
      const l = user.Lastname || user.lastName || user.LastName || '';
      return `${f} ${l}`.trim();
    },
    isOwn(msg) {
      return msg && msg.UserID === (this.profile && this.profile.UserID);
    },
    preview(chat) {
      if (!chat || !chat.Messages || !chat.Messages.length)
        return this.$t('Components.ChatDock.previewNoMessages');
      const last = chat.Messages[chat.Messages.length - 1];
      const ownId = this.profile && this.profile.UserID;
      const text = this.stripHtml(last.Message || '').trim();
      const short = text.length > 32 ? text.slice(0, 30) + '…' : text;
      if (last.UserID === ownId) {
        return this.$t('Components.ChatDock.youPrefix', {
          message: short || '…'
        });
      }
      return short || '…';
    },
    lastAt(chat) {
      if (!chat || !chat.Messages || !chat.Messages.length) return null;
      const last = chat.Messages[chat.Messages.length - 1];
      return last && (last.CreatedAt || last.createdAt);
    },
    formatTime(date) {
      if (!date) return '';
      return Vue.moment(date).format('h:mm A');
    },
    formatDay(date) {
      if (!date) return '';
      const now = Vue.moment();
      const m = Vue.moment(date);
      if (m.isSame(now, 'day')) return m.format('HH:mm');
      if (m.isSame(now, 'week')) return m.format('ddd');
      return m.format('DD/MM');
    },
    // ── Window controls ───────────────────────────────────────────
    toggleMinimize(w) {
      this.setDockWindowMinimized({
        userId: w.userId,
        minimized: !w.minimized
      });
      if (w.minimized) this.scrollToBottom(w.userId);
    },
    expandWindow(w) {
      this.setDockWindowMinimized({ userId: w.userId, minimized: false });
      this.scrollToBottom(w.userId);
      this.markReadFor(w.userId);
    },
    // Clear unread for a conversation the user is now looking at — drops
    // the unread dot/badge (locally + on the server).
    markReadFor(userId) {
      const chat = this.chatFor(userId);
      if (chat && chat.ChatID && this.unreadInChat(chat) > 0) {
        this.markChatRead({ chatid: chat.ChatID }).catch(() => {});
      }
    },
    closeWindow(w) {
      this.openMenuId = null;
      this.closeDockWindow(w.userId);
    },
    toggleMenu(id) {
      this.openMenuId = this.openMenuId === id ? null : id;
    },
    openFullView(w) {
      this.openMenuId = null;
      const user = this.userFor(w.userId);
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        PrivateChatModal,
        { recipient: user },
        {
          adaptive: true,
          clickToClose: true,
          width: '95%',
          height: '85%',
          minWidth: 320,
          maxWidth: 1100
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    },
    // ── Conversation options (… menu) ─────────────────────────────
    markRead(w) {
      this.openMenuId = null;
      const chat = this.chatFor(w.userId);
      if (!chat || !chat.ChatID) return;
      this.markChatRead({ chatid: chat.ChatID }).catch(() => {});
    },
    viewProfile(w) {
      this.openMenuId = null;
      const user = this.userFor(w.userId);
      if (!user || !user.UserID) return;
      // Redirect to the platform's existing profile page, scoped to this
      // participant so it shows THEIR info (not the current user's).
      this.$router
        .push({ name: 'profile', query: { userId: String(user.UserID) } })
        .catch(() => {
          /* duplicate-nav errors are harmless */
        });
    },
    deleteConversation(w) {
      this.openMenuId = null;
      const name = this.fullName(this.userFor(w.userId));
      // No server-side delete endpoint exists yet — this removes the
      // conversation window from the dock locally. (Wire a backend
      // DELETE here if/when the API supports it.)
      const ok =
        typeof window === 'undefined' ||
        window.confirm(
          name
            ? this.$t('Components.ChatDock.confirmRemove', { name })
            : this.$t('Components.ChatDock.confirmRemoveGeneric')
        );
      if (!ok) return;
      this.$set(this.drafts, w.userId, '');
      this.closeDockWindow(w.userId);
    },
    // ── Inbox controls ────────────────────────────────────────────
    toggleInbox() {
      this.setInboxMinimized(!this.inboxMinimized);
    },
    expandInbox() {
      this.setInboxMinimized(false);
    },
    toggleCompose() {
      this.composeMode = !this.composeMode;
      this.expandInbox();
      // Make sure the attendee list is loaded so "Start a new chat"
      // (and its search) actually has people to show — the dock may
      // have been created before users were fetched.
      if (this.composeMode) this.ensureUsers();
    },
    closeInbox() {
      this.openMenuId = false;
      this.setInboxOpen(false);
    },
    refreshAndCloseMenu() {
      this.openMenuId = null;
      this.loadChatData().catch(() => {});
    },
    openWith(user) {
      if (!user || !user.UserID) return;
      this.composeMode = false;
      this.search = '';
      this.openDockWindow(user);
      this.$nextTick(() => {
        this.scrollToBottom(user.UserID);
        this.markReadFor(user.UserID);
      });
    },
    openFromChat(chat) {
      this.openWith(this.counterpart(chat));
    },
    // ── Compose / send ────────────────────────────────────────────
    setDraft(userId, val) {
      this.$set(this.drafts, userId, val);
    },
    async resolveChat(userId) {
      let chat = this.chatFor(userId);
      if (!chat) {
        try {
          const res = await this.createNewChat(userId);
          chat = res && res.data;
          await this.loadChatData();
          chat = this.chatFor(userId) || chat;
        } catch (e) {
          /* ignore */
        }
      }
      if (chat && chat.ChatID) this.activateChat(chat);
      return chat;
    },
    async send(w) {
      const text = (this.drafts[w.userId] || '').trim();
      if (!text || this.sendingId) return;
      this.sendingId = w.userId;
      try {
        const chat = await this.resolveChat(w.userId);
        if (!chat || !chat.ChatID) return;
        await this.sendChatMessage({ chatid: chat.ChatID, message: text });
        this.$set(this.drafts, w.userId, '');
        await this.loadChatData();
        this.scrollToBottom(w.userId);
      } catch (e) {
        /* ignore */
      } finally {
        this.sendingId = null;
      }
    },
    // ── Scrolling ─────────────────────────────────────────────────
    scrollToBottom(userId) {
      this.$nextTick(() => {
        if (!this.$el || !this.$el.querySelector) return;
        const el = this.$el.querySelector(
          '.cdock-win__msgs[data-user="' + userId + '"]'
        );
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    scrollAllToBottom() {
      this.$nextTick(() => {
        if (!this.$el || !this.$el.querySelectorAll) return;
        this.$el.querySelectorAll('.cdock-win__msgs').forEach((el) => {
          el.scrollTop = el.scrollHeight;
        });
      });
    },
    // ── Safe message rendering (matches PrivateChatModal) ──────────
    stripHtml(raw) {
      if (!raw) return '';
      const div = document.createElement('div');
      div.innerHTML = raw;
      return div.textContent || div.innerText || '';
    },
    renderMessage(raw) {
      if (!raw) return '';
      const allowedImg =
        /<img\s+src="(data:image\/[a-z+]+;base64,[a-zA-Z0-9+/=]+)"(?:\s+alt="([^"]*)")?(?:\s+class="pcm-msg-img")?\s*\/?>/gi;
      const allowedFile =
        /<a\s+href="(data:[^"]+)"\s+download="([^"]+)"\s+class="pcm-msg-file">\s*<span class="pcm-msg-file-name">([^<]+)<\/span>\s*<span class="pcm-msg-file-size">([^<]+)<\/span>\s*<\/a>/gi;
      const br = /<br\s*\/?>(\s*)/gi;
      const tokens = [];
      let working = raw;
      working = working.replace(allowedImg, (m, src, alt) => {
        const i = tokens.length;
        tokens.push(
          `<img src="${src}" alt="${this.escapeHtml(
            alt || 'image'
          )}" class="pcm-msg-img" />`
        );
        return `@@T${i}@@`;
      });
      working = working.replace(allowedFile, (m, href, dl, name, size) => {
        const i = tokens.length;
        tokens.push(
          `<a href="${href}" download="${this.escapeHtml(
            dl
          )}" class="pcm-msg-file">` +
            `<span class="pcm-msg-file-name">${this.escapeHtml(name)}</span>` +
            `<span class="pcm-msg-file-size">${this.escapeHtml(size)}</span>` +
            `</a>`
        );
        return `@@T${i}@@`;
      });
      working = working.replace(br, () => {
        const i = tokens.length;
        tokens.push('<br>');
        return `@@T${i}@@`;
      });
      working = this.escapeHtml(working);
      working = working.replace(
        /@@T(\d+)@@/g,
        (_, i) => tokens[parseInt(i, 10)] || ''
      );
      return working;
    },
    escapeHtml(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }
};
</script>

<style scoped lang="scss">
$cdock-win-w: 320px;
$cdock-head-h: 48px;

.cdock {
  position: fixed;
  right: 20px;
  bottom: 0;
  // Above page content but BELOW modal overlays (vue-js-modal uses 999),
  // so "View profile" / "Open full view" modals open ON TOP of the dock
  // instead of behind it.
  z-index: 900;
  display: flex;
  align-items: flex-end;
  gap: 16px;
  font-family: 'Poppins', sans-serif;
  pointer-events: none; // let gaps pass clicks through to the page

  > * {
    pointer-events: auto;
  }

  // Keep overflow visible so each window's options (…) dropdown can
  // pop outside the window/rail bounds without being clipped. Width is
  // capped to the viewport; with few concurrent windows this is enough,
  // and the gaps stay click-through to the page.
  max-width: calc(100vw - 40px);
  overflow: visible;
}

.cdock-win {
  width: $cdock-win-w;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  height: 420px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  // NOTE: intentionally NOT `overflow: hidden` — that would clip the
  // options (…) dropdown. The scrollable message area clips itself, and
  // the header rounds its own top corners to keep the card look.

  &--min {
    height: $cdock-head-h;
    width: 260px;
  }
}

.cdock-win__head {
  height: $cdock-head-h;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 0 8px 0 12px;
  border-bottom: 1px solid #eef0f2;
  background: #ffffff;
  cursor: pointer;
  // Round the top corners (the window no longer clips via overflow).
  border-radius: 12px 12px 0 0;
}

.cdock-win__id {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.cdock-win__name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cdock-badge {
  flex-shrink: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #dc2626;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cdock-win__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.cdock-icon-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: background 0.12s, color 0.12s;

  &:hover {
    background: #f3f4f6;
    color: var(--color-primary, #047873);
  }

  // Active/toggled state — used by the Compose button while the
  // "Start a new chat" list is open so it reads as selected.
  &--active {
    background: var(--color-primary, #047873);
    color: #ffffff;

    &:hover {
      background: var(--color-primary-hover, #036663);
      color: #ffffff;
    }
  }
}

// ── Avatars ───────────────────────────────────────────────────────
.cdock-avatar {
  position: relative;
  width: 30px;
  height: 30px;
  flex-shrink: 0;

  &--sm {
    width: 36px;
    height: 36px;
  }

  &--group {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-primary, #047873);
    color: #fff;
    border-radius: 50%;
    font-size: 14px;
  }
}

.cdock-avatar__img {
  width: 100% !important;
  height: 100% !important;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;

  ::v-deep img,
  ::v-deep .avatar {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    border-radius: 50%;
  }
}

.cdock-dot {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid #ffffff;
}

// ── Options menu ──────────────────────────────────────────────────
.cdock-menu-wrap {
  position: relative;
}

.cdock-menu {
  position: absolute;
  bottom: calc(100% + 6px);
  // The … trigger lives in the right-hand action cluster, and the whole
  // dock hugs the right edge of the viewport — so anchor the menu to the
  // right and let it open leftward into the window. This keeps it from
  // being cut off by the viewport edge.
  right: 0;
  left: auto;
  min-width: 184px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  padding: 6px;
  z-index: 50;
  pointer-events: auto;

  &--right {
    left: auto;
    right: 0;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    background: none;
    border-radius: 8px;
    font-size: 13px;
    color: #374151;
    cursor: pointer;
    text-align: left;
    font-family: 'Poppins', sans-serif;

    i {
      width: 14px;
      font-size: 12px;
      color: #6b7280;
    }

    &:hover {
      background: #f3f4f6;
    }

    &--danger {
      color: #dc2626;
      i {
        color: #dc2626;
      }
      &:hover {
        background: #fee2e2;
      }
    }
  }
}

// ── Messages ──────────────────────────────────────────────────────
.cdock-win__msgs {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 8px;
  scrollbar-width: thin;
}

.cdock-msg {
  max-width: 80%;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &--own {
    align-self: flex-end;
    align-items: flex-end;

    .cdock-bubble {
      background: var(--color-primary, #047873);
      color: #fff;
      border-bottom-right-radius: 4px;
    }
    .cdock-msg__time {
      text-align: right;
    }
  }

  &--other {
    align-self: flex-start;
    .cdock-bubble {
      border-bottom-left-radius: 4px;
    }
  }
}

.cdock-bubble {
  background: #ffffff;
  color: #111827;
  border: 1px solid #e5e7eb;
  padding: 8px 11px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.4;
  word-wrap: break-word;

  ::v-deep img.pcm-msg-img {
    display: block;
    max-width: 200px;
    max-height: 200px;
    border-radius: 8px;
    margin: 2px 0;
  }

  ::v-deep a.pcm-msg-file {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    margin: 2px 0;
    background: #f3f4f6;
    border-radius: 8px;
    text-decoration: none;
    color: inherit;
    font-size: 12px;
  }
}

.cdock-msg__time {
  font-size: 10px;
  color: #9ca3af;
  padding: 0 2px;
}

.cdock-empty {
  margin: auto;
  text-align: center;
  color: #9ca3af;

  i {
    font-size: 26px;
    color: #d1d5db;
    display: block;
    margin-bottom: 8px;
  }
  p {
    margin: 0;
    font-size: 12px;
    line-height: 1.5;
  }
}

// ── Composer ──────────────────────────────────────────────────────
.cdock-input {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-top: 1px solid #eef0f2;
  background: #fff;
}

.cdock-input__field {
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  background: #f3f4f6;
  border: 1.5px solid #d1d5db;
  border-radius: 18px;
  font-size: 13px;
  font-family: 'Poppins', sans-serif;
  outline: none;

  &::placeholder {
    color: #9ca3af;
  }
  &:focus {
    border-color: var(--color-primary, #047873);
    background: #fff;
  }
}

.cdock-send {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 50%;
  border: none;
  background: var(--color-primary, #047873);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

// ── Inbox window ──────────────────────────────────────────────────
.cdock-inbox {
  width: 300px;
}

.cdock-inbox__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  scrollbar-width: thin;
}

.cdock-search {
  position: relative;
  padding: 10px 12px;
  flex-shrink: 0;

  i {
    position: absolute;
    top: 50%;
    left: 24px;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 12px;
  }

  input {
    width: 100%;
    padding: 8px 12px 8px 32px;
    background: #f3f4f6;
    border: 1.5px solid #d1d5db;
    border-radius: 18px;
    font-size: 13px;
    font-family: 'Poppins', sans-serif;
    outline: none;

    &:focus {
      border-color: var(--color-primary, #047873);
      background: #fff;
    }
  }
}

.cdock-list-label {
  padding: 6px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
}

.cdock-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font-family: 'Poppins', sans-serif;
  transition: background 0.12s;

  &:hover {
    background: #f3f4f6;
  }

  &__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__name {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Blue unread dot in front of the sender's name.
  &__unread-dot {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
  }

  &__preview {
    font-size: 12px;
    color: #6b7280;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    flex-shrink: 0;
  }

  &__time {
    font-size: 10px;
    color: #9ca3af;
  }

  // Unread count badge (right side).
  &__count {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9999px;
    background: var(--color-primary, #047873);
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  // Unread conversations read bolder/darker so they stand out.
  &--unread {
    .cdock-row__name {
      color: #0f172a;
      font-weight: 700;
    }
    .cdock-row__preview {
      color: #374151;
      font-weight: 600;
    }
  }
}

.cdock-inbox__empty {
  text-align: center;
  padding: 28px 14px;
  color: #9ca3af;

  i {
    font-size: 24px;
    color: #d1d5db;
    display: block;
    margin-bottom: 8px;
  }
  p {
    margin: 0;
    font-size: 12px;
  }
}

// ── Responsive ────────────────────────────────────────────────────
@media (max-width: 768px) {
  .cdock {
    display: none;
  } // mobile uses the full-page /chat route
}

// ── Dark mode ─────────────────────────────────────────────────────
// Stamped on <body data-theme="dark"> by the Theme store. The dock
// hard-codes light hex in its scoped rules above; these body-scoped
// overrides have higher specificity so they win without !important.
body[data-theme='dark'] {
  // Window shell + header
  .cdock-win {
    background: var(--t-surface);
    border-color: var(--t-border);
    box-shadow: var(--t-card-shadow-lg);
  }
  .cdock-win__head {
    background: var(--t-surface);
    border-bottom-color: var(--t-divider);
  }
  .cdock-win__name {
    color: var(--t-text);
  }

  // Header icon buttons
  .cdock-icon-btn {
    color: var(--t-text-secondary);

    &:hover {
      background: var(--t-surface-alt);
      color: var(--t-primary);
    }
  }

  // Options dropdown
  .cdock-menu {
    background: var(--t-surface-elevated);
    border-color: var(--t-border-strong);
    box-shadow: var(--t-card-shadow-lg);

    &__item {
      color: var(--t-text-secondary);

      i {
        color: var(--t-text-muted);
      }
      &:hover {
        background: var(--t-surface-alt);
      }
      &--danger {
        color: var(--t-danger);
        i {
          color: var(--t-danger);
        }
        &:hover {
          background: var(--t-danger-soft);
        }
      }
    }
  }

  // Avatars + presence dot
  .cdock-avatar__img {
    background: var(--t-surface-alt);
  }
  .cdock-dot {
    border-color: var(--t-surface);
  }

  // Message list + bubbles
  .cdock-win__msgs {
    background: var(--t-surface-sunken);
  }
  .cdock-bubble {
    background: var(--t-surface-elevated);
    color: var(--t-text);
    border-color: var(--t-border);
  }
  // Own (sent) bubble keeps the brand teal — just ensure white text.
  .cdock-msg--own .cdock-bubble {
    background: var(--t-btn);
    color: var(--t-btn-on);
    border-color: transparent;
  }
  .cdock-msg__time {
    color: var(--t-text-muted);
  }
  .cdock-empty,
  .cdock-inbox__empty {
    color: var(--t-text-muted);

    i {
      color: var(--t-text-disabled);
    }
  }

  // Composer
  .cdock-input {
    background: var(--t-surface);
    border-top-color: var(--t-divider);
  }
  .cdock-input__field {
    background: var(--t-input-bg);
    border-color: var(--t-input-border);
    color: var(--t-input-text);

    &::placeholder {
      color: var(--t-input-placeholder);
    }
    &:focus {
      background: var(--t-input-bg-focus);
      border-color: var(--t-input-border-focus);
    }
  }

  // Inbox: search + lists
  .cdock-search input {
    background: var(--t-input-bg);
    border-color: var(--t-input-border);
    color: var(--t-input-text);

    &:focus {
      background: var(--t-input-bg-focus);
      border-color: var(--t-input-border-focus);
    }
  }
  .cdock-list-label {
    color: var(--t-text-muted);
  }
  .cdock-row {
    &:hover {
      background: var(--t-surface-alt);
    }
    &__name {
      color: var(--t-text);
    }
    &__preview,
    &__time {
      color: var(--t-text-secondary);
    }
    &__unread-dot {
      background: var(--t-primary);
    }
    &__count {
      background: var(--t-btn);
      color: var(--t-btn-on);
    }
    &--unread {
      .cdock-row__name {
        color: var(--t-text);
      }
      .cdock-row__preview {
        color: var(--t-text);
      }
    }
  }
}
</style>
